import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, deleteDoc, writeBatch, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaGripVertical, FaChevronDown, FaChevronUp, FaTags } from 'react-icons/fa';
import CreateLabelModal from './CreateLabelModal';
import ConfirmationModal from '../ConfirmationModal';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Label Item Component
const SortableLabel = ({ label, selectedLabel, onLabelClick, isAdmin, isDeletable, onDeleteClick, isReorderMode }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ 
    id: label.id,
    disabled: label.isSpecial // Disable sorting for special labels
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const postCount = label.postCount || 0;
  
  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="flex items-center group"
    >
      {isAdmin && isReorderMode && !label.isSpecial && (
        <div 
          className="cursor-grab active:cursor-grabbing px-1 text-gray-400"
          {...attributes} 
          {...listeners}
        >
          <FaGripVertical />
        </div>
      )}
      <button
        onClick={() => onLabelClick(label)}
        className={`flex-1 text-left px-3 py-2 rounded-lg transition-colors ${
          label.isSpecial
            ? selectedLabel === label.labelId
              ? label.isFollowing
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-2 border-green-400 shadow-lg transform scale-105'
                : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-2 border-indigo-400 shadow-lg transform scale-105'
              : label.isFollowing
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 hover:from-green-100 hover:to-emerald-100 dark:hover:from-green-800/30 dark:hover:to-emerald-800/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/50'
                : 'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-800/30 dark:hover:to-purple-800/30 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/50'
            : selectedLabel === label.labelId
              ? 'bg-violet-200 text-violet-700 border border-violet-200'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}
      >
        <div className="flex justify-between items-center">
          <span className={label.isSpecial ? 'font-medium' : ''}>{label.labelName}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {postCount}
          </span>
        </div>
      </button>
      {isAdmin && !isReorderMode && !label.isSpecial && (
        <button
          className={`ml-2 p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors ${
            isDeletable ? 'text-red-500' : 'text-gray-400 cursor-not-allowed opacity-50'
          }`}
          disabled={!isDeletable}
          onClick={() => {
            if (isDeletable) {
              onDeleteClick(label);
            }
          }}
          title={isDeletable ? 'Delete label' : 'Cannot delete label with posts'}
        >
          <FaTrash />
        </button>
      )}
    </div>
  );
};

function ForumSidePanel({ selectedLabel, onLabelSelect }) {
  const { currentUser } = useAuth();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreateLabelModal, setShowCreateLabelModal] = useState(false);
  const [labelPostCounts, setLabelPostCounts] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isLabelsExpanded, setIsLabelsExpanded] = useState(false);
  
  // Set up dnd sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (!currentUser) return;

    // Check admin status
    const checkAdminStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data().isAdmin || false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminStatus();

    // Set up labels listener
    const labelsQuery = query(
      collection(db, 'forumLabels'),
      orderBy('sorting', 'asc'),
      orderBy('labelName')
    );

    const unsubscribeLabels = onSnapshot(labelsQuery, (snapshot) => {
      const labelsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sorting: doc.data().sorting || 9999
      }));
      setLabels(labelsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching labels:', error);
      toast.error('Failed to load labels');
      setLoading(false);
    });

    // Set up posts listener for counting
    const postsQuery = query(
      collection(db, 'posts'),
      where('postStatus', '!=', 'removed')
    );

    const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
      const counts = {};
      let adminPostCount = 0;
      
      snapshot.docs.forEach(doc => {
        const post = doc.data();
        
        // Count admin posts
        if (post.isAdmin) {
          adminPostCount++;
        }
        
        if (post.postLabels && Array.isArray(post.postLabels)) {
          post.postLabels.forEach(labelId => {
            counts[labelId] = (counts[labelId] || 0) + 1;
          });
        }
      });
      
      // Add admin count to the counts object
      counts['admin'] = adminPostCount;
      
      // Get user's following posts and count how many exist
      const getUserFollowingCount = async () => {
        try {
          const followingDoc = await getDoc(doc(db, 'followingPosts', currentUser.uid));
          if (followingDoc.exists()) {
            const followingData = followingDoc.data();
            const followingPosts = followingData.posts || [];
            
            // Count how many of the followed posts actually exist in the current posts
            const existingFollowedPosts = snapshot.docs.filter(postDoc => 
              followingPosts.includes(postDoc.id) && postDoc.data().postStatus !== 'removed'
            );
            
            counts['following'] = existingFollowedPosts.length;
          } else {
            counts['following'] = 0;
          }
          
          setLabelPostCounts({...counts});
        } catch (error) {
          console.error('Error getting following count:', error);
          counts['following'] = 0;
          setLabelPostCounts({...counts});
        }
      };
      
      getUserFollowingCount();
    }, (error) => {
      console.error('Error fetching post counts:', error);
    });

    // Set up listener for user's following posts document to update count in real-time
    const followingDocRef = doc(db, 'followingPosts', currentUser.uid);
    const unsubscribeFollowing = onSnapshot(followingDocRef, async (followingSnapshot) => {
      // When the following document changes, update the following count
      try {
        if (followingSnapshot.exists()) {
          const followingData = followingSnapshot.data();
          const followingPostIds = followingData.posts || [];
          
          // Get current posts to count how many followed posts exist
          const postsSnapshot = await getDocs(query(
            collection(db, 'posts'),
            where('postStatus', '!=', 'removed')
          ));
          
          const existingFollowedPosts = postsSnapshot.docs.filter(postDoc => 
            followingPostIds.includes(postDoc.id)
          );
          
          const followingCount = existingFollowedPosts.length;
          
          setLabelPostCounts(prevCounts => ({
            ...prevCounts,
            following: followingCount
          }));

          // If user is currently viewing "My Following" and count becomes 0, unselect the filter
          if (selectedLabel === 'following' && followingCount === 0) {
            // Use setTimeout to ensure this happens after the current execution cycle
            setTimeout(() => {
              onLabelSelect(null);
              toast.info('No more followed posts - filter cleared');
            }, 100);
          }
        } else {
          // No following document exists
          setLabelPostCounts(prevCounts => ({
            ...prevCounts,
            following: 0
          }));

          // If user is currently viewing "My Following" and document doesn't exist, unselect the filter
          if (selectedLabel === 'following') {
            // Use setTimeout to ensure this happens after the current execution cycle
            setTimeout(() => {
              onLabelSelect(null);
              toast.info('No more followed posts - filter cleared');
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error updating following count:', error);
        setLabelPostCounts(prevCounts => ({
          ...prevCounts,
          following: 0
        }));
      }
    }, (error) => {
      // Document might not exist yet, which is fine
      if (error.code !== 'not-found') {
        console.error('Error listening to following posts:', error);
      }
    });

    return () => {
      unsubscribeLabels();
      unsubscribePosts();
      unsubscribeFollowing();
    };
  }, [currentUser, selectedLabel]);

  const handleLabelClick = (label) => {
    if (isReorderMode) return; // Prevent label selection while reordering
    const newLabel = selectedLabel === label.labelId ? null : label.labelId;
    onLabelSelect(newLabel);
    // On mobile, collapse the dropdown after selection
    if (window.innerWidth < 768) {
      setIsLabelsExpanded(false);
    }
  };

  const handleDeleteLabel = async () => {
    if (!labelToDelete) return;
    try {
      await deleteDoc(doc(db, 'forumLabels', labelToDelete.id));
      toast.success('Label deleted successfully');
    } catch (error) {
      console.error('Error deleting label:', error);
      toast.error('Failed to delete label');
    } finally {
      setShowDeleteModal(false);
      setLabelToDelete(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Find the actual items from the labels array
      const oldIndex = labels.findIndex(label => label.id === active.id);
      const newIndex = labels.findIndex(label => label.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Create a new array with the items in the new order
        const newLabels = arrayMove(labels, oldIndex, newIndex);
        
        // Update sorting values
        const updatedLabels = newLabels.map((label, index) => ({
          ...label,
          sorting: index * 10 // Use step of 10 to allow for future insertions
        }));
        
        // Update UI immediately
        setLabels(updatedLabels);
        
        // Update in Firestore
        try {
          const batch = writeBatch(db);
          
          updatedLabels.forEach(label => {
            const labelRef = doc(db, 'forumLabels', label.id);
            batch.update(labelRef, { sorting: label.sorting });
          });
          
          await batch.commit();
          toast.success('Label order updated');
        } catch (error) {
          console.error('Error updating label order:', error);
          toast.error('Failed to update label order');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
      </div>
    );
  }

  // Process labels to include post counts
  const labelItems = labels.map(label => ({
    ...label,
    postCount: labelPostCounts[label.labelId] || 0
  }));

  // Add special "Admin" label at the top
  const adminLabel = {
    id: 'admin-special',
    labelId: 'admin',
    labelName: 'Admin',
    postCount: labelPostCounts['admin'] || 0, // Use the actual count from labelPostCounts
    isSpecial: true
  };

  // Add special "My Following" label
  const followingLabel = {
    id: 'following-special',
    labelId: 'following',
    labelName: 'My Following',
    postCount: labelPostCounts['following'] || 0, // This will be calculated based on user's following
    isSpecial: true,
    isFollowing: true // Flag to differentiate from admin label
  };

  const allLabelItems = [adminLabel, followingLabel, ...labelItems];

  // Get selected label name for mobile display
  const selectedLabelName = selectedLabel 
    ? allLabelItems.find(l => l.labelId === selectedLabel)?.labelName || selectedLabel
    : 'All Topics';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
      {/* Mobile View - Collapsible Dropdown */}
      <div className="block md:hidden">
        <button 
          className="w-full flex justify-between items-center p-3 bg-purple-50 dark:bg-gray-700 rounded-lg"
          onClick={() => setIsLabelsExpanded(!isLabelsExpanded)}
        >
          <div className="flex items-center gap-2">
            <FaTags className="text-purple-500" />
            <span className="font-medium">
              {selectedLabelName}
            </span>
          </div>
          {isLabelsExpanded ? 
            <FaChevronUp className="text-gray-500" /> : 
            <FaChevronDown className="text-gray-500" />
          }
        </button>
        
        {isLabelsExpanded && (
          <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-800 shadow-lg">
            <div className="space-y-1">
              {allLabelItems.map((label, index) => (
                <div key={label.id}>
                  <button
                    onClick={() => handleLabelClick(label)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      label.isSpecial
                        ? selectedLabel === label.labelId
                          ? label.isFollowing
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-2 border-green-400 shadow-lg transform scale-105'
                            : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-2 border-indigo-400 shadow-lg transform scale-105'
                          : label.isFollowing
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 hover:from-green-100 hover:to-emerald-100 dark:hover:from-green-800/30 dark:hover:to-emerald-800/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/50'
                            : 'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-800/30 dark:hover:to-purple-800/30 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/50'
                        : selectedLabel === label.labelId
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className={label.isSpecial ? 'font-medium' : ''}>{label.labelName}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {label.postCount || 0}
                      </span>
                    </div>
                  </button>
                  {/* Add separator after following label (both special labels) */}
                  {label.isSpecial && index === 1 && labelItems.length > 0 && (
                    <hr className="my-3 border-gray-200 dark:border-gray-700" />
                  )}
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => setShowCreateLabelModal(true)}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded-lg flex items-center gap-2 text-sm transition-colors"
                >
                  <FaPlus />
                  Add Label
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Desktop View - Regular Sidebar */}
      <div className="hidden md:block">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Labels</h2>
        {isAdmin && (
          <div className="flex gap-2">
            {isReorderMode ? (
              <button
                onClick={() => setIsReorderMode(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition-colors"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsReorderMode(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                >
                  Reorder
                </button>
                <button
                  onClick={() => setShowCreateLabelModal(true)}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded-lg flex items-center gap-2 text-sm transition-colors"
                >
                  <FaPlus />
                  Add Label
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {isReorderMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={allLabelItems.map(l => l.id)}
              strategy={verticalListSortingStrategy}
            >
              {allLabelItems.map((label, index) => (
                <div key={label.id}>
                  <SortableLabel
                    label={label}
                    selectedLabel={selectedLabel}
                    onLabelClick={handleLabelClick}
                    isAdmin={isAdmin}
                    isDeletable={label.postCount === 0}
                    onDeleteClick={() => setLabelToDelete(label)}
                    isReorderMode={isReorderMode}
                  />
                  {/* Add separator after following label (both special labels) */}
                  {label.isSpecial && index === 1 && labelItems.length > 0 && (
                    <hr className="my-3 border-gray-200 dark:border-gray-700" />
                  )}
                </div>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          allLabelItems.map((label, index) => (
            <div key={label.id}>
              <SortableLabel
                label={label}
                selectedLabel={selectedLabel}
                onLabelClick={handleLabelClick}
                isAdmin={isAdmin}
                isDeletable={label.postCount === 0}
                onDeleteClick={() => setLabelToDelete(label)}
                isReorderMode={false}
              />
              {/* Add separator after following label (both special labels) */}
              {label.isSpecial && index === 1 && labelItems.length > 0 && (
                <hr className="my-3 border-gray-200 dark:border-gray-700" />
              )}
            </div>
          ))
        )}
        </div>
      </div>

      <CreateLabelModal
        isOpen={showCreateLabelModal}
        onClose={() => setShowCreateLabelModal(false)}
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setLabelToDelete(null);
        }}
        onConfirm={handleDeleteLabel}
        title="Delete Label"
        message={`Are you sure you want to delete the label "${labelToDelete?.labelName}"? This action cannot be undone.`}
      />
    </div>
  );
}

export default ForumSidePanel;
